'use client';

import React, { useState } from 'react';
import {
  Download,
  Share2,
  ClipboardList,
  Copy,
  FileText,
} from 'lucide-react';
import { useQuery, useLazyQuery } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  DOCUMENT_QUERY,
  DOCUMENT_PDF_URL_QUERY,
  type Document,
  type DocumentLine,
  type MaterialTemplateItemInput,
  DocumentType as DocumentTypeEnum,
  LineType,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import {
  useCart,
  RequestListBanner,
} from '@paeffgen/react/crm-documents/feature-material-requests';
import { SaveToProjectModal } from '@paeffgen/react/crm-documents/feature-favorites';
import { Spinner, useNavHighlight, HighlightText } from '@paeffgen/react/crm-documents/ui';
import { formatCurrency, exportLineItemsToCsv, getDocumentLineAnzahl } from '@paeffgen/react/crm-documents/util';
import { usePdfShare } from './use-pdf-share';
import { DocumentChain } from './document-chain';
import { DocumentLines } from './document-lines';
import { clsx } from 'clsx';

interface DocumentDetailProps {
  /** The summary document from the list (may not have lines loaded). */
  document: Document;
  /** Articlenumber to highlight inside the positions table. */
  highlightArticleNumber?: string | null;
  /** Positions-Auswahl (Key = lineNumber) — Owner ist DocumentDetailDialog,
   *  damit Tabelle und Aktionsleiste dieselbe Auswahl teilen. */
  selected?: Set<string>;
  setSelected?: (
    next: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
}

export function DocumentDetail({
  document,
  highlightArticleNumber,
  selected,
  setSelected,
}: DocumentDetailProps) {
  const { adminQueryVars, adminMutationVars, isAdmin, allCustomers, isSingleCustomerContext } = useAdmin();
  const showDebitor = isAdmin && allCustomers;
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const { markHighlight } = useNavHighlight();
  const needsDetail = !document.lines || document.lines.length === 0;

  const { data: detailData, loading: detailLoading } = useQuery<{
    document: Document;
  }>(DOCUMENT_QUERY, {
    variables: { id: document.id, ...adminQueryVars },
    skip: !needsDetail,
    fetchPolicy: 'cache-first',
  });

  const [fetchPdfUrl, { loading: pdfLoading }] = useLazyQuery<{
    documentPdfUrl: string;
  }>(DOCUMENT_PDF_URL_QUERY, {
    fetchPolicy: 'network-only',
  });

  const fullDocument = needsDetail && detailData?.document
    ? detailData.document
    : document;

  const allLines = fullDocument.lines ?? [];

  // Lieferscheine: hide lines without quantity (not actually delivered), but
  // keep TEXT hint lines (e.g. "Bei selbstständiger Abholung von Ware") — they
  // carry no quantity yet appear on the PDF and must stay visible in the modal.
  const lines = fullDocument.type === DocumentTypeEnum.LIEFERSCHEIN
    ? allLines.filter((l) => l.quantity != null || l.lineType === LineType.TEXT)
    : allLines;

  const handleDownloadPdf = async () => {
    if (!fullDocument.pdfKey) return;
    const result = await fetchPdfUrl({
      variables: { documentId: fullDocument.id, ...adminQueryVars },
    });
    if (result.data?.documentPdfUrl) {
      window.open(result.data.documentPdfUrl, '_blank');
    }
  };

  const { canShare, isSharing, sharePdf } = usePdfShare(adminQueryVars);
  const handleSharePdf = () => {
    if (!fullDocument.pdfKey) return;
    sharePdf(fullDocument.id, fullDocument.documentNumber);
  };

  const { addItems } = useCart();

  // Selektions-bewusste Aktionen (spiegelt MobileDocumentDetailFooter):
  // ohne Auswahl = alle Artikelpositionen, mit Auswahl = nur die markierten.
  const articleLines = lines.filter(
    (line) =>
      line.lineType === LineType.ARTICLE && line.articleNumber && !line.isReturn
  );
  const hasSelection = !!selected && selected.size > 0;
  const targetLines = hasSelection
    ? articleLines.filter((line) => selected!.has(line.lineNumber))
    : articleLines;
  const primaryLabel = hasSelection
    ? `${targetLines.length} zur Anfrage`
    : 'Alle zur Anfrage';

  const handleAddToCartAll = () => {
    const itemsToAdd = targetLines.map((line) => ({
      articleNumber: line.articleNumber!,
      // Anfrage-Input ist die Anzahl (VE-Stück), nicht die Gesamtmenge — sonst
      // multipliziert das Backend den VE-Faktor ein zweites Mal ein.
      quantity: getDocumentLineAnzahl(line),
    }));
    if (itemsToAdd.length === 0) {
      toast.warning('Keine Artikel auswählbar');
      return;
    }
    addItems(itemsToAdd);
    // Auswahl nach dem Hinzufügen auflösen — nur wenn explizit selektiert war
    // (bei „Alle zur Anfrage" gibt es nichts zu leeren).
    if (hasSelection) setSelected?.(new Set());
  };

  const handleExportCSV = () => {
    if (targetLines.length === 0) return;
    exportLineItemsToCsv(
      [{ ...fullDocument, lines: targetLines }],
      `Positionen_${fullDocument.documentNumber}.csv`
    );
  };

  const templateItems: MaterialTemplateItemInput[] = targetLines.map((line) => ({
    articleNumber: line.articleNumber!,
    quantity: line.quantity ?? 1,
  }));

  if (detailLoading) {
    return (
      <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex items-center justify-center min-h-[200px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50/50 border-t border-gray-100">
      {/* Top Meta Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 text-sm">
        <div>
          <h4 className="text-gray-500 font-medium mb-1">
            Referenz / Kommission:
          </h4>
          {fullDocument.yourReference && (
            <p className="font-semibold text-gray-900">
              <HighlightText text={fullDocument.yourReference} />
            </p>
          )}
          {fullDocument.commission ? (
            <p className="font-semibold text-gray-900 truncate" title={fullDocument.commission}>
              <HighlightText text={fullDocument.commission} />
            </p>
          ) : !fullDocument.yourReference ? (
            <p className="font-semibold text-gray-900">-</p>
          ) : null}
        </div>

        <div>
          <h4 className="text-gray-500 font-medium mb-1">IDM:</h4>
          <p className="font-semibold text-gray-900">
            {fullDocument.systemId ? (
              <HighlightText text={fullDocument.systemId} />
            ) : (
              '-'
            )}
          </p>
        </div>

        <div>
          <h4 className="text-gray-500 font-medium mb-1">Buchungsdatum:</h4>
          <p className="font-semibold text-gray-900">
            {fullDocument.orderDate || '-'}
          </p>
        </div>

        {showDebitor && (
          <div>
            <h4 className="text-gray-500 font-medium mb-1">Debitor:</h4>
            <p
              className="font-semibold text-gray-900 truncate"
              title={
                fullDocument.customerNumber && fullDocument.customerName
                  ? `${fullDocument.customerNumber} — ${fullDocument.customerName}`
                  : undefined
              }
            >
              {fullDocument.customerNumber && fullDocument.customerName
                ? `${fullDocument.customerNumber} — ${fullDocument.customerName}`
                : (fullDocument.customerNumber ?? fullDocument.customerName ?? '-')}
            </p>
          </div>
        )}
      </div>

      {/* Chain Visualization */}
      <DocumentChain documentId={fullDocument.id} />

      {/* Positions Header */}
      <div className="mb-4">
        <h3 className="font-bold text-gray-900">
          Positionen (
          {lines.filter((l) => l.lineType === LineType.ARTICLE).length})
        </h3>
      </div>

      {/* Lines Table */}
      <DocumentLines
        lines={lines}
        documentId={fullDocument.id}
        documentType={fullDocument.type}
        netUnitPriceStatus={fullDocument.netUnitPriceStatus}
        highlightArticleNumber={highlightArticleNumber}
        selected={selected}
        setSelected={setSelected}
      />

      {/* Totals — only for document types that carry pricing */}
      {fullDocument.type !== DocumentTypeEnum.LIEFERSCHEIN && (
        <div className="flex justify-end mt-6">
          <div className="w-64 space-y-2">
            <div className="flex justify-between text-gray-600">
              <span>Netto</span>
              <span className="font-medium tabular-nums">
                {formatCurrency(fullDocument.netTotal ?? 0)}
              </span>
            </div>
            <div className="flex justify-between text-sm text-gray-500">
              <span>MwSt. (19%)</span>
              <span className="tabular-nums">
                {formatCurrency(
                  (fullDocument.grossTotal ?? 0) - (fullDocument.netTotal ?? 0)
                )}
              </span>
            </div>
            <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
              <span>Brutto</span>
              <span className="tabular-nums text-primary">
                {formatCurrency(fullDocument.grossTotal ?? 0)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="mt-10 pt-6 border-t border-gray-200">
        {isSingleCustomerContext && <RequestListBanner className="mb-4 max-w-md" />}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-4">
          <h3 className="text-sm font-bold text-gray-700">Aktionsleiste:</h3>
          {hasSelection && (
            <span className="text-xs text-gray-500">
              {targetLines.length} von {articleLines.length} Positionen ausgewählt
              <button
                type="button"
                onClick={() => setSelected?.(new Set())}
                className="ml-2 text-primary hover:underline font-medium"
              >
                Auswahl aufheben
              </button>
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleAddToCartAll}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover shadow-sm transition-colors text-sm font-medium"
          >
            <ClipboardList size={18} />
            {primaryLabel}
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm hover:bg-gray-50 hover:text-primary transition-colors"
          >
            <FileText size={18} />
            Positionen als CSV exportieren
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={!fullDocument.pdfKey || pdfLoading}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors',
              fullDocument.pdfKey && !pdfLoading
                ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-primary'
                : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            {pdfLoading ? <Spinner size="sm" /> : <Download size={18} />}
            PDF herunterladen
          </button>
          {canShare && fullDocument.pdfKey && (
            <button
              onClick={handleSharePdf}
              disabled={isSharing}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors',
                isSharing
                  ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-primary'
              )}
            >
              {isSharing ? <Spinner size="sm" /> : <Share2 size={18} />}
              Teilen
            </button>
          )}
          <button
            onClick={() => setIsTemplateModalOpen(true)}
            disabled={templateItems.length === 0}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors',
              templateItems.length > 0
                ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-primary'
                : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            <Copy size={18} />
            Als Projekt speichern
          </button>
        </div>
      </div>

      <SaveToProjectModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        items={templateItems}
        selectedCount={templateItems.length}
        initialName={fullDocument.documentNumber}
        queryVars={adminQueryVars}
        mutationVars={adminMutationVars}
        onSaved={() => {
          markHighlight('projekte');
          setIsTemplateModalOpen(false);
        }}
      />
    </div>
  );
}
